import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { toast } from 'sonner'
import { Mail, Plus, Edit2, Save, X, Eye, Code, Send } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

interface EmailTemplate {
  id: string
  template_key: string
  name: string
  subject: string
  html_content: string
  variables: string[]
  is_active: boolean
  created_at: string
  updated_at: string
}

export const EmailTemplateManager = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null)
  const [newTemplate, setNewTemplate] = useState(false)
  const [showTestDialog, setShowTestDialog] = useState(false)
  const [testEmail, setTestEmail] = useState('')
  const [testTemplateKey, setTestTemplateKey] = useState('')
  const [testTenantId, setTestTenantId] = useState('')
  const [tenants, setTenants] = useState<any[]>([])
  const [formData, setFormData] = useState({
    template_key: '',
    name: '',
    subject: '',
    html_content: '',
    variables: [] as string[],
    is_active: true
  })

  useEffect(() => {
    fetchTemplates()
    fetchTenants()
  }, [])

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      
      const formattedData = data?.map(template => ({
        ...template,
        variables: Array.isArray(template.variables) ? template.variables : 
                  typeof template.variables === 'string' ? JSON.parse(template.variables) : []
      })) || []
      
      setTemplates(formattedData)
    } catch (error: any) {
      toast.error('Fehler beim Laden der E-Mail-Vorlagen: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchTenants = async () => {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select('id, name, slug')
        .eq('status', 'active')
        .order('name')

      if (error) throw error
      setTenants(data || [])
    } catch (error: any) {
      console.error('Error fetching tenants:', error)
    }
  }

  const handleSave = async () => {
    try {
      if (!formData.template_key || !formData.name || !formData.subject || !formData.html_content) {
        toast.error('Bitte füllen Sie alle Pflichtfelder aus')
        return
      }

      const templateData = {
        ...formData,
        variables: JSON.stringify(formData.variables)
      }

      let error
      if (editingTemplate) {
        const { error: updateError } = await supabase
          .from('email_templates')
          .update(templateData)
          .eq('id', editingTemplate.id)
        error = updateError
      } else {
        const { error: insertError } = await supabase
          .from('email_templates')
          .insert([templateData])
        error = insertError
      }

      if (error) throw error

      toast.success(editingTemplate ? 'Vorlage aktualisiert' : 'Vorlage erstellt')
      setEditingTemplate(null)
      setNewTemplate(false)
      resetForm()
      fetchTemplates()
    } catch (error: any) {
      toast.error('Fehler beim Speichern: ' + error.message)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Sind Sie sicher, dass Sie diese Vorlage löschen möchten?')) return

    try {
      const { error } = await supabase
        .from('email_templates')
        .delete()
        .eq('id', id)

      if (error) throw error
      toast.success('Vorlage gelöscht')
      fetchTemplates()
    } catch (error: any) {
      toast.error('Fehler beim Löschen: ' + error.message)
    }
  }

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('email_templates')
        .update({ is_active: !isActive })
        .eq('id', id)

      if (error) throw error
      toast.success('Status aktualisiert')
      fetchTemplates()
    } catch (error: any) {
      toast.error('Fehler beim Aktualisieren: ' + error.message)
    }
  }

  const resetForm = () => {
    setFormData({
      template_key: '',
      name: '',
      subject: '',
      html_content: '',
      variables: [],
      is_active: true
    })
  }

  const startEdit = (template: EmailTemplate) => {
    setEditingTemplate(template)
    const variables = Array.isArray(template.variables) ? template.variables : 
                     typeof template.variables === 'string' ? JSON.parse(template.variables) : []
    setFormData({
      template_key: template.template_key,
      name: template.name,
      subject: template.subject,
      html_content: template.html_content,
      variables: variables,
      is_active: template.is_active
    })
  }

  const addVariable = (variable: string) => {
    if (variable && !formData.variables.includes(variable)) {
      setFormData(prev => ({
        ...prev,
        variables: [...prev.variables, variable]
      }))
    }
  }

  const removeVariable = (variable: string) => {
    setFormData(prev => ({
      ...prev,
      variables: prev.variables.filter(v => v !== variable)
    }))
  }

  const sendTestEmail = async () => {
    if (!testEmail || !testTemplateKey) {
      toast.error('Bitte füllen Sie alle Felder aus')
      return
    }

    try {
      const { data, error } = await supabase.functions.invoke('test-email-templates', {
        body: {
          template_key: testTemplateKey,
          test_email: testEmail,
          tenant_id: testTenantId || undefined
        }
      })

      if (error) throw error

      toast.success('Test-E-Mail erfolgreich versendet!')
      setShowTestDialog(false)
      setTestEmail('')
      setTestTemplateKey('')
      setTestTenantId('')
    } catch (error: any) {
      toast.error('Fehler beim Versenden der Test-E-Mail: ' + error.message)
    }
  }

  const getPreviewHtml = () => {
    let preview = formData.html_content
    formData.variables.forEach(variable => {
      const regex = new RegExp(`{{${variable}}}`, 'g')
      preview = preview.replace(regex, `[${variable.toUpperCase()}]`)
    })
    return preview
  }

  if (loading) {
    return <div className="flex justify-center p-8">Lädt...</div>
  }

  return (
    <div className="space-y-6">
      {/* Header with Summary Cards */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">E-Mail-Vorlagen</h2>
          <p className="text-muted-foreground">
            Verwalten Sie E-Mail-Vorlagen, die von allen Admins verwendet werden
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowTestDialog(true)}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Send className="h-4 w-4" />
            Test-E-Mail senden
          </Button>
          <Button 
            onClick={() => {
              setNewTemplate(true)
              resetForm()
            }}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Neue Vorlage
          </Button>
        </div>
      </div>

      {/* Email Template System Overview */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-950 dark:to-indigo-900 p-6 rounded-lg border">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Mail className="h-5 w-5 text-blue-600" />
          Email Template System
        </h3>
        <div className="grid gap-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
              <div className="text-sm font-medium text-green-700 dark:text-green-400">Aktive Templates</div>
              <div className="text-2xl font-bold">{templates.filter(t => t.is_active).length}</div>
            </div>
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
              <div className="text-sm font-medium text-blue-700 dark:text-blue-400">Gesamt Templates</div>
              <div className="text-2xl font-bold">{templates.length}</div>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Verwalten Sie E-Mail-Vorlagen für Auth-Emails, Einladungen und mehr. 
            Alle Templates unterstützen Tenant-Branding mit Logo und Farben.
          </p>
        </div>
      </div>

      {/* Template List */}
      <div className="grid gap-4">
        {templates.map((template) => (
          <Card key={template.id}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    {template.name}
                    {template.is_active ? (
                      <Badge variant="default">Aktiv</Badge>
                    ) : (
                      <Badge variant="secondary">Inaktiv</Badge>
                    )}
                  </CardTitle>
                  <CardDescription>
                    Schlüssel: {template.template_key} | Betreff: {template.subject}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Vorschau: {template.name}</DialogTitle>
                      </DialogHeader>
                      <Tabs defaultValue="preview">
                        <TabsList>
                          <TabsTrigger value="preview">Vorschau</TabsTrigger>
                          <TabsTrigger value="code">HTML Code</TabsTrigger>
                        </TabsList>
                        <TabsContent value="preview">
                          <div 
                            className="border rounded p-4 bg-white"
                            dangerouslySetInnerHTML={{ 
                              __html: template.html_content.replace(/{{(\w+)}}/g, '[$1]') 
                            }}
                          />
                        </TabsContent>
                        <TabsContent value="code">
                          <pre className="text-sm bg-muted p-4 rounded overflow-x-auto">
                            <code>{template.html_content}</code>
                          </pre>
                        </TabsContent>
                      </Tabs>
                    </DialogContent>
                  </Dialog>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => startEdit(template)}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Switch
                    checked={template.is_active}
                    onCheckedChange={() => handleToggleActive(template.id, template.is_active)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Label className="text-sm text-muted-foreground">Variablen:</Label>
                {template.variables?.map((variable: string) => (
                  <Badge key={variable} variant="outline">
                    {`{{${variable}}}`}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit/New Template Dialog */}
      <Dialog open={editingTemplate !== null || newTemplate} onOpenChange={(open) => {
        if (!open) {
          setEditingTemplate(null)
          setNewTemplate(false)
          resetForm()
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Vorlage bearbeiten' : 'Neue Vorlage erstellen'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="template_key">Vorlagen-Schlüssel *</Label>
                <Input
                  id="template_key"
                  value={formData.template_key}
                  onChange={(e) => setFormData(prev => ({ ...prev, template_key: e.target.value }))}
                  placeholder="z.B. referral_invitation"
                  disabled={editingTemplate !== null}
                />
              </div>
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="z.B. Empfehlungseinladung"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="subject">Betreff *</Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="z.B. {{tenant_name}} lädt Sie ein"
              />
            </div>

            <div>
              <Label>Variablen verwalten</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Variable hinzufügen (z.B. tenant_name)"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      addVariable(e.currentTarget.value)
                      e.currentTarget.value = ''
                    }
                  }}
                />
                <Button
                  type="button"
                  onClick={(e) => {
                    const input = e.currentTarget.previousSibling as HTMLInputElement
                    addVariable(input.value)
                    input.value = ''
                  }}
                >
                  Hinzufügen
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.variables.map((variable) => (
                  <Badge key={variable} variant="outline" className="flex items-center gap-1">
                    {`{{${variable}}}`}
                    <X 
                      className="h-3 w-3 cursor-pointer" 
                      onClick={() => removeVariable(variable)}
                    />
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="html_content">HTML Inhalt *</Label>
              <Textarea
                id="html_content"
                value={formData.html_content}
                onChange={(e) => setFormData(prev => ({ ...prev, html_content: e.target.value }))}
                placeholder="HTML Inhalt der E-Mail..."
                className="min-h-[300px] font-mono text-sm"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
              <Label htmlFor="is_active">Vorlage aktiv</Label>
            </div>

            <Separator />
            
            <div>
              <Label>Vorschau</Label>
              <div 
                className="border rounded p-4 bg-white max-h-64 overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: getPreviewHtml() }}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setEditingTemplate(null)
                setNewTemplate(false)
                resetForm()
              }}
            >
              Abbrechen
            </Button>
            <Button onClick={handleSave} className="flex items-center gap-2">
              <Save className="h-4 w-4" />
              Speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Test-E-Mail senden</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="test_template">E-Mail-Vorlage *</Label>
              <Select value={testTemplateKey} onValueChange={setTestTemplateKey}>
                <SelectTrigger>
                  <SelectValue placeholder="Vorlage auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {templates.filter(t => t.is_active).map((template) => (
                    <SelectItem key={template.id} value={template.template_key}>
                      {template.name} ({template.template_key})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="test_email">Test E-Mail-Adresse *</Label>
              <Input
                id="test_email"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
              />
            </div>

            <div>
              <Label htmlFor="test_tenant">Tenant (optional)</Label>
              <Select value={testTenantId} onValueChange={setTestTenantId}>
                <SelectTrigger>
                  <SelectValue placeholder="Standard / Kein Tenant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Standard (kein Tenant)</SelectItem>
                  {tenants.map((tenant) => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {tenant.name} ({tenant.slug})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowTestDialog(false)}
            >
              Abbrechen
            </Button>
            <Button onClick={sendTestEmail} className="flex items-center gap-2">
              <Send className="h-4 w-4" />
              Test-E-Mail senden
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}